import {Component} from '@angular/core';
import {MemberService} from '../../../services/member.service';
import {MemberModel, ProjectSettings} from '../../../models/user.model';
import {TokenService} from '../../../services/token.service';
import {ModalService} from '../../../services/modal/modal.service';
import {AuthService} from '../../../services/api/auth.service';
import {AlertService} from '../../../services/notify/alert.service';
import {Router} from '@angular/router';
import {CompanyModel} from '../../../models/company.model';
import {HelpersService} from '../../../services/helpers.service';
import {TranslateService} from '@ngx-translate/core';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {DomSanitizer} from '@angular/platform-browser';

@Component({
    selector: 'app-profile-control',
    templateUrl: './profile-control.component.html',
    styleUrls: ['./profile-control.component.scss'],
})
export class ProfileControlComponent {
    public profile: MemberModel;
    public selectedProject: string;
    public myCompanies: ProjectSettings[];
    public contrAgents: ProjectSettings[];

    public tab = 'profile';

    public oldPin: string = null;
    public newPin: string = null;
    public oldExtraPin: string = null;
    public newExtraPin: string = null;

    public currentLanguage: string;

    public selectedImage: Blob | MediaSource;
    public hasAvatar: boolean = false;
    public loadingAvatar = false;

    constructor(
        protected memberService: MemberService,
        protected tokenService: TokenService,
        protected modalService: ModalService,
        protected auth: AuthService,
        protected alert: AlertService,
        protected router: Router,
        public helper: HelpersService,
        private translate: TranslateService,
    ) {
        const storedLang = localStorage.getItem('language');
        this.currentLanguage = storedLang || 'ru';

        this.memberService.getCurrentMember().then(data => {
            this.profile = data;
            this.selectedProject = data.project_setting.project.guid;
            this.myCompanies = data.projects_list.filter(prj => prj.is_project_admin);
            this.contrAgents = data.projects_list.filter(prj => !prj.is_project_admin);
        });
    }

    saveEditProfile(lName: string, fName: string, mName: string, phoneNumber: string, language: string): void {
        this.auth.editUserProfile({
            firstname: fName,
            middlename: mName,
            lastname: lName,
            phone: phoneNumber,
            lang: language,
        }).subscribe({
            next: res => {
                this.switchLanguage(language);
                this.profile = res.member;
                this.alert.setAlert(this.translate.instant('ALERTS.SUCCESS'), 'green');
            }, error: error => {
                this.alert.setAlert(error.message, 'blue');
            },
        });
    }

    switchLanguage(language: string): void {
        this.translate.use(language);
        localStorage.setItem('language', language);
        this.currentLanguage = language;
    }

    changePassword(oldPass: string, newPass: string, checkNewPass: string): void {
        if (!oldPass || !newPass || !checkNewPass) {
            return this.alert.setAlert(this.translate.instant('ALERTS.EMPTY_PASS'), 'blue');
        }
        if (newPass !== checkNewPass) {
            return this.alert.setAlert(this.translate.instant('ALERTS.ERROR_PASS'), 'blue');
        }
        if (oldPass === newPass) {
            return this.alert.setAlert(this.translate.instant('ALERTS.UNIQ_PASS'), 'blue');
        }

        const data = {
            passwd_old: oldPass,
            passwd_new: newPass,
        };

        this.auth.updatePassword(data).subscribe({
            next: res => {
                this.tokenService.setToken(res.access_token, res.expires_in);
                this.alert.setAlert(this.translate.instant('ALERTS.SUCCESS'), 'green');
            }, error: error => {
                this.alert.setAlert(error.message, 'blue');
            },
        });
    }

    changePin(): void {
        const data = {
            old_pin: this.oldPin,
            new_pin: this.newPin,
        };
        this.auth.updatePin(data).subscribe({
            next: res => {
                this.oldPin = null;
                this.newPin = null;
                this.alert.setAlert(res.message, 'green');
            }, error: err => {
                this.oldPin = null;
                this.newPin = null;
                this.alert.setAlert(err.message, 'blue');
            }
        });
    }

    changeExtraPin(): void {
        const data = {
            old_pin: this.oldExtraPin,
            new_pin: this.newExtraPin,
        };
        this.auth.updateExtraPin(data).subscribe({
            next: res => {
                this.oldExtraPin = null;
                this.newExtraPin = null;
                this.alert.setAlert(res.message, 'green');
            }, error: err => {
                this.oldExtraPin = null;
                this.newExtraPin = null;
                this.alert.setAlert(err.message, 'blue');
            }
        });
    }

    goLink(prj: CompanyModel | ProjectSettings, contract = null): void {
        const schema = window.location.href.split('://')[0];
        let baseDomain: any;
        let bodyModal: string = null;
        if (contract) {
            baseDomain = contract.master_project.domain;
            bodyModal = this.translate.instant('CONFIRM.TEXT.GO_CONTRACT', {name: contract.master_project.name});
        } else {
            if ('project' in prj) {
                baseDomain = prj.project.domain;
                bodyModal = this.translate.instant('CONFIRM.TEXT.CHANGE_PROJECT', {name: prj.project.name});
            }
        }
        const url = schema + '://' + baseDomain + '/login';

        const q = new URLSearchParams({
            auth_token: this.tokenService.getToken(),
            lastpage: contract ? ('account/contract/' + contract.guid) : 'account/options/profile',
        });
        if (this.profile.project_setting.project.domain !== baseDomain) {
            const modalSettings: any = {
                modalHeader: this.translate.instant('CONFIRM.HEADERS.ATTENTION'),
                modalBody: bodyModal,
                buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
                buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
            };
            const modal = this.modalService.createFromSettings(modalSettings);
            modal.result.then(data => {
                if (data) {
                    document.location.href = url + '?' + q.toString();
                }
            }).catch(() => {
                return;
            });
        } else {
            if (contract) {
                void this.router.navigate(['account', 'contracts', 'detail', contract.guid]);
            } else {
                return;
            }
        }
    }

    viewImageUpload(data: Blob | MediaSource): void {
        this.selectedImage = data;
        const output = document.getElementById('output');
        console.log(output);
        const src = 'src';
        if (output) {
            output[src] = URL.createObjectURL(data);
            output.onload = () => {
                URL.revokeObjectURL(output[src]);
            };
        }
    }

    changeAvatar(file: Blob | MediaSource, modal: NgbModalRef): void {
        const image = {
            avatar: file,
        };
        this.auth.updateAvatar(image).subscribe({
            next: res => {
                this.selectedImage = null;
                this.alert.setAlert(res.message, 'green');
                this.loadingAvatar = true;
                const timeout = setTimeout(() => {
                    this.loadingAvatar = false;
                    clearTimeout(timeout);
                }, 1500);
                modal.close();
            }, error: err => {
                this.alert.setAlert(err.message, 'blue');
                console.log(err);
            }
        });
    }

    deleteAvatar(): void {
        const modalSettings = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.DELETE'),
            modalBody: this.translate.instant('CONFIRM.TEXT.DELETE_AVATAR'),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
            buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(res => {
            if (res) {
                this.auth.deleteAvatar().subscribe({
                    next: res => {
                        this.loadingAvatar = true;
                        this.alert.setAlert(res.message, 'green');
                        const timeout = setTimeout(() => {
                            this.loadingAvatar = false;
                            clearTimeout(timeout);
                        }, 1500);
                    }, error: err => {
                        this.alert.setAlert(err.message, 'blue');
                    }
                });
            }
        }).catch(() => {
            return;
        });
    }
}
